import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import typeOptionValue from "../../i18n/type_option_value.json"; // Adjust the path accordingly
import GradeLevelSelector from "./GradeLevelSelector";
import LocationSelector from "./LocationSelector";
import SubjectSelector from "./SubjectSelector";

const AdForm = ({ id }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const auth = useSelector((state) => state.auth); // Retrieve user data
  const user = auth.user; // Safely retrieve user ID
  const userId = user?.id; // Safely retrieve user ID
  const [adType] = useState(user.role);
  const [selectedSubjects, setSelectedSubjects] = useState([]); // Multi-select for Subject
  const [selectedGrades, setSelectedGrades] = useState([]); // Multi-select for grade levels
  const [SelectedLocations, setSelectedLocations] = useState([]); // Multi-select for regions
  const [selectedSubjectTypes, setSelectedSubjectTypes] = useState([]);
  const [selectedFormGroup, setSelectedFormGroup] = useState([]);
  const [selectedFeeType, setselectedFeeType] = useState("one_hour");
  const [selectedLesson, setselectedLesson] = useState("1");
  const [fee, setFee] = useState("");
  const [requirements, setRequirements] = useState("");
  const [expirationDate, setExpirationDate] = useState(
    new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  );
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [isEditMode] = useState(!!id); // Determine if in edit mode

  useEffect(() => {
    const fetchData = async () => {
      if (isEditMode) {
        try {
          const token = auth.token;
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/ads.php`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              params: {
                id: id,
              },
            }
          );
          const ad = response.data.ad;

          console.log(adType);
          // Reset form
          setSelectedSubjectTypes(JSON.parse(ad.subjectType));
          setSelectedSubjects(JSON.parse(ad.subject));
          setSelectedGrades(JSON.parse(ad.gradeLevel));
          setSelectedLocations(JSON.parse(ad.location));
          setFee(ad.fee);
          setRequirements(ad.requirements);
          setSelectedFormGroup(JSON.parse(ad.formGroupType));
          setselectedFeeType(ad.feeType);
          setselectedLesson(ad.weekLesson ? ad.weekLesson : 1);
          setExpirationDate(ad.expirationDate.split("T")[0]);
        } catch (error) {
          setError("Error fetching data:", error);
          console.error("Error fetching data:", error);
        }
      }
    };

    fetchData();
  }, [id, isEditMode, adType, auth.token]);

  const validateForm = () => {
    if (selectedFormGroup.length === 0) {
      toast.error(t("ad.validation.formGroupTypeRequired"));
      return false;
    }
    if (selectedGrades.length === 0) {
      toast.error(t("ad.validation.gradeRequired"));
      return false;
    }
    if (selectedSubjectTypes.length === 0) {
      toast.error(t("ad.validation.subjectTypeRequired"));
      return false;
    }
    if (selectedSubjects.length === 0) {
      toast.error(t("ad.validation.subjectRequired"));
      return false;
    }
    if (SelectedLocations.length === 0) {
      toast.error(t("ad.validation.locationRequired"));
      return false;
    }
    if (!fee) {
      toast.error(t("ad.validation.feeRequired"));
      return false;
    }
    if (!selectedLesson && adType === "student") {
      toast.error(t("ad.validation.lessonRequired"));
      return false;
    }
    if (!requirements.length > 1) {
      toast.error(t("ad.validation.requirementsequired"));
      return false;
    }
    if (!expirationDate) {
      toast.error(t("ad.validation.expirationDateRequired"));
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return; // Stop submission if validation fails
    }

    setStatus("loading");

    try {
      const adData = {
        id: id,
        subjectType: JSON.stringify(selectedSubjectTypes),
        subject: JSON.stringify(selectedSubjects),
        gradeLevel: JSON.stringify(selectedGrades),
        location: JSON.stringify(SelectedLocations),
        formGroupType: JSON.stringify(selectedFormGroup),
        feeType: selectedFeeType,
        fee: parseFloat(fee),
        requirements,
        weekLesson: selectedLesson,
        adType,
        expirationDate,
        userId,
      };
      const token = localStorage.getItem("token"); // Get token from localStorage
      if (isEditMode) {
        // Update existing record
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/ads.php`,
          adData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              id: id,
            },
          }
        );
        toast.success(t("ad.adUpdatedSuccess"));
      } else {
        // Create new record
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/ads.php`,
          adData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(t("ad.adCreatedSuccess"));
      }

      setStatus("succeeded");
      // Reset form
      setSelectedSubjectTypes([]);
      setSelectedSubjects([]);
      setSelectedGrades([]);
      setSelectedLocations([]);
      setFee("");
      setRequirements("");
      setSelectedFormGroup([]);
      setselectedFeeType("one_hour");
      setExpirationDate("");
      navigate("/myAds");
    } catch (error) {
      console.error("Error creating ad:", error);
      setError(error.response?.data?.message || error.message);
      toast.error(t("ad.adCreationFailed"));
      setStatus("failed");
    }
  };

  const handleSubjectChange = (value) => {
    setSelectedSubjectTypes((prev) =>
      prev.includes(value)
        ? prev.filter((subject) => subject !== value)
        : [...prev, value]
    );
  };
  const handleLessonChange = (event) => {
    setselectedLesson(event.target.value);
  };
  const handleFeeChange = (event) => {
    setselectedFeeType(event.target.value);
  };
  const handleFormGroupChange = (value) => {
    setSelectedFormGroup((prev) =>
      prev.includes(value)
        ? prev.filter((group) => group !== value)
        : [...prev, value]
    );
  };

  if (status === "loading") {
    return <div>{t("ad.loading")}</div>;
  }

  if (status === "failed") {
    return (
      <div>
        {t("ad.error")}: {error}
      </div>
    );
  }

  return (
    <div className="form-container">
      <h2>
        {t(adType)} {t("createAd")}
      </h2>
      <form onSubmit={handleSubmit}>
        <label>{t("ad.formGroupType")}:</label>
        <div className="flex flex-wrap ">
          {Object.entries(typeOptionValue.form_group_types).map(
            ([key, { display }]) => (
              <span
                key={key}
                onClick={() => handleFormGroupChange(key)}
                className={`Select-tag    ${
                  selectedFormGroup.includes(key)
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-800"
                }`}
              >
                {display[t("language")]}
              </span>
            )
          )}
        </div>
        <GradeLevelSelector
          selectedGrades={selectedGrades}
          setSelectedGrades={setSelectedGrades} // Ensure this is a function
        />

        <label>{t("ad.subjectType")}:</label>
        <div className="flex flex-wrap  ">
          {Object.entries(typeOptionValue.subject_types).map(
            ([key, { display }]) => (
              <span
                key={key}
                onClick={() => handleSubjectChange(key)}
                className={`Select-tag    ${
                  selectedSubjectTypes.includes(key)
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-800"
                }`}
              >
                {display[t("language")]}
              </span>
            )
          )}
        </div>
        <SubjectSelector
          selectedSubjects={selectedSubjects}
          setSelectedSubjects={setSelectedSubjects}
        />
        <LocationSelector
          SelectedLocations={SelectedLocations}
          setSelectedLocations={setSelectedLocations}
        />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
          {adType === "student" && (
            <div>
              <label>
                {t("ad.weekLesson")}:
                <select value={selectedLesson} onChange={handleLessonChange}>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                  <option value="6">6</option>
                  <option value="7">7</option>
                </select>
              </label>
            </div>
          )}
          <div>
            <label>
              {t("ad.feeType")}:
              <select value={selectedFeeType} onChange={handleFeeChange}>
                {Object.entries(typeOptionValue.fee_types).map(
                  ([key, { display }]) => (
                    <option key={key} value={key}>
                      {display[t("language")]}
                    </option>
                  )
                )}
              </select>
            </label>
          </div>
          <div>
            <label>
              {t("ad.fee")}:
              <input
                type="number"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
              />
            </label>
          </div>
          <div>
            <label>
              {t("ad.expirationDate")}:
              <div className="p-2">{expirationDate}</div>
            </label>
          </div>
        </div>
        <label>{t("ad.requirements")}:</label>
        <textarea
          value={requirements}
          rows="10"
          onChange={(e) => setRequirements(e.target.value)}
        />
        <label></label>
        <button type="submit">{t("createAd")}</button>
      </form>
    </div>
  );
};

export default AdForm;
